import { DialogProps } from '@mui/material';
import invariant from 'invariant';
import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react';

export interface ModalContext {
  leftActionsContainer?: HTMLDivElement;
  rightActionsContainer?: HTMLDivElement;
  blocked: boolean;
  setBlocked: (blocked: boolean) => void;
  setBlockerCallback: (callback: (blocked: boolean) => void) => void;
  proceed: () => void;
}

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
export const modalContext = createContext<ModalContext>(undefined!);

export const useModal = () => {
  const context = useContext(modalContext);
  invariant(context, 'useModal should only be used inside Modal component');
  return context;
};

export interface ModalProps extends Omit<DialogProps, 'title' | 'onClose' | 'children' | 'scroll'> {
  size: 'small' | 'medium' | 'large';
  className?: string;
  children?: ReactNode;
  onClose?: () => void;
  disableBackdropClick?: boolean;
  disableEscapeKey?: boolean;
  disableCloseButton?: boolean;
}

export const useModalBlocker = (blocked: boolean) => {
  const { setBlocked, setBlockerCallback, proceed } = useModal();

  const [isBlocking, setIsBlocking] = useState(false);

  useEffect(() => {
    setBlocked(blocked);
  }, [blocked, setBlocked]);

  useEffect(() => {
    setBlockerCallback(() => setIsBlocking);
  }, [setBlockerCallback]);

  const reset = useCallback(() => {
    setIsBlocking(false);
  }, []);

  return { isBlocking, proceed, reset };
};
